import _ from "lodash";
import Vuex from "vuex";
import axios from "axios";
import http from "js/campaigns/campaign-manager/http";
import {MUTATION_ASSIGN_CHART} from "js/campaigns/campaign-manager/mutation-types";

export default new Vuex.Store({
  state: {
    isLoading: false,
    campaignId: window.campaignId,
    campaignData: window.campaignData,
    adSets: [],
    campaignDeactivatedByLastAdSet: false,
    stats: {},
    todaysStats: null,
    bidStats: null,
    chart: {startDate: null, endDate: null, isChartUpdating: false}
  },
  getters: {
    isLoading: (state) => state.isLoading,
    statsPeriodTotals: (state) => state.stats.periodTotals,
    chartStartDate: (state) => state.chart.startDate,
    chartEndDate: (state) => state.chart.endDate,
    chartIsUpdating: (state) => state.chart.isChartUpdating,

    statsItemTotals (state) {
      const { stats: { itemTotals } } = state;

      if (typeof itemTotals === 'undefined') {
        return {};
      }
      return itemTotals;
    },

    adSets: function (state) {
      return state.adSets;
    },

    adSetIds: function (state) {
      if (!state.adSets) {
        return [];
      }

      return state.adSets.map(function (adSet) {
        return adSet.id;
      });
    },

    bidIds (state) {
      if (!state.adSets) {
        return [];
      }

      return state.adSets.map(adSet => {
        return adSet.bids.map(bid => bid.id);
      }).reduce((acc, curr) => acc.concat(curr), []);
    },

    todaysAdSetStats (state) {
      if (!state.todaysStats || !state.todaysStats.adSets) {
        return {};
      }

      return _.chain(state.todaysStats.adSets)
        .map(({ id, click_count, cost }) => ({ id, todays_click_count: click_count, todays_cost: cost }))
        .keyBy('id')
        .value();
    },

    todaysBidsStats (state) {
      if (!state.todaysStats || !state.todaysStats.bids) {
        return {};
      }

      return _.chain(state.todaysStats.bids)
        .map(({ id, click_count, cost }) => ({ id, todays_click_count: click_count, todays_cost: cost }))
        .keyBy('id')
        .value();
    },

    bidStats (state) {
      if (!state.bidStats) {
        return {};
      }
      return _.keyBy(state.bidStats.itemTotals, 'id');
    },

    composedAdSets (state, getters) {
      if (!state.adSets) return [];
      return _.chain(state.adSets)
        .map(adSet => {
          const selectedStats = _.find(getters.statsItemTotals, { id: adSet.id });
          const composedAdSet = Object.assign({}, adSet, selectedStats, getters.todaysAdSetStats[adSet.id]);
          composedAdSet.bids = composedAdSet.bids.map(bid => {
            return Object.assign({}, bid, getters.bidStats[bid.id], getters.todaysBidsStats[bid.id]);
          });
          return composedAdSet;
        }).value();
    },

    getBidNamesById (state) {
      return (bidIds) => {
        return state.adSets.reduce((carry, adSet) => {
            adSet.bids.filter(bid => bidIds.includes(bid.id)).forEach(bid => carry.push(bid.name));
          return carry
        }, [])
      }
    },

      isCostPerClick (state) {
        if(!state.campaignData){
          return false;
        }
        return state.campaignData.ad_product.type === 'CPC';
      },

      isCostPerLead (state) {
          if(!state.campaignData){
            return false;
          }
          return state.campaignData.ad_product.type === 'CPL';
      },

      isBankrateSelect (state) {
        return state.campaignData.blnBankrateSelect === 1;
      },

      currentCampaign (state) {
        return state.campaignData;
      }
  },

  actions: {
    fetchAdSets: function (context, payload) {
      return axios.get(payload.baseUrl + '/api/campaign/' + payload.campaignId + '/adsets')
        .then(function (response) {
          context.commit('assignAdSets', response.data.adSets);
          context.commit('notLoadingAdSets');

          if (typeof payload.callback === 'function') {
            payload.callback();
          }
        })
        .catch(function () {
          context.commit('notLoadingAdSets');

          if (typeof payload.callback === 'function') {
            payload.callback();
          }
        });
    },

    requestBidStats ({ commit, getters }, { start, end, interval }) {
      if (!getters.bidIds.length) {
        return
      }
      const params = { start, end, interval };

      params.bids = getters.bidIds.join(',');

      return http.get('/api/reports/click-cost', { params }).then(response => {
        commit('bidStats', response.data.data);
      });
    },

    requestTodaysStats ({ getters, commit }) {
      const params = {};
      if (getters.adSetIds.length) {
        params.adsets = getters.adSetIds.join(',');
      }

      if (getters.bidIds.length) {
        params.bids = getters.bidIds.join(',');
      }

      return http.get('/api/reports/todays-click-cost', { params }).then(response => {
        commit('todaysStats', response.data.data);
      });
    },

    changeBidStatus ({ state, commit }, bidId) {
      const adSet = state.adSets.find(adSet => adSet.bids.some(bid => bid.id === bidId));
      const bid = adSet.bids.find(bid => bid.id === bidId);
      const action = bid.is_active ? 'deactivate' : 'activate';

      commit('changeBidStatus', bid);

      return http.put(`/campaigns/${state.campaignId}/adset/${adSet.id}/bids/${bidId}/${action}`)
          .catch(error => {
            commit('changeBidStatus', bid);
            return Promise.reject(error);
        })
    }
  },


  mutations: {
    [MUTATION_ASSIGN_CHART] (state, {startDate, endDate, isChartUpdating}) {
      state.chart = {...state.chart, startDate, endDate, isChartUpdating};
    },

    setIsLoading (state, payload) {
      state.isLoading = payload;
    },

    assignAdSets (state, payload) {
      state.adSets = payload;
    },

    setStats (state, payload) {
      state.stats = payload;
    },

    clearStats (state) {
      state.stats = {};
    },

    todaysStats (state, payload) {
      state.todaysStats = payload;
    },

    bidStats (state, payload) {
      state.bidStats = payload;
    },

    setAdSetIsActive (state, payload) {
      const adSet = _.find(state.adSets, { id: payload.id });
      adSet.is_active = payload.isActive;
    },

    setAdSetIgnoreSchedule (state, payload) {
      const adSet = _.find(state.adSets, { id: payload.id });
      adSet.ignore_schedule = payload.ignoreSchedule;
    },

    deactivateCampaignByLastAdSet (state, payload) {
      state.campaignDeactivatedByLastAdSet = payload;
    },

    changeBidStatus (state, bid) {
      bid.is_active = !bid.is_active
    }
  }
});
